import { formatWaiting, minutesSince } from "./priority";
import type { AnsweredChat, PendingChat, PendingCounts } from "./api";

/**
 * The Pulse — the old Home's situational awareness, rebuilt on the queue's own data.
 * The worklist is a FIRE QUEUE: it collapses to "all clear" when nothing is breaching,
 * and a blank screen tells a CM nothing about the state of play. The Pulse is the
 * always-on answer to "what's happening?" — what's pending right now, and what the
 * doctors have just answered (AND whether that answer actually reached the parent) —
 * so the landing is never empty and never a lie.
 *
 * DISPLAY-ONLY. Two reads (pending counts, recently-answered), no action, no link that
 * sends. Both are optional: a failed/absent read renders an honest empty state, never a
 * fake zero, and never touches the fire queue above it.
 */

const TYPE_LABEL: Record<string, string> = {
  qna: "Q&A",
  doctor_care: "Doctor care",
  followup: "Follow-up",
  lactation: "Lactation",
};

function typeLabel(t: PendingChat["type"]): string {
  return TYPE_LABEL[t] ?? t;
}

const PENDING_ROWS: Array<{ key: keyof PendingCounts; label: string }> = [
  { key: "qna", label: "Q&A" },
  { key: "doctor_care", label: "Doctor care" },
  { key: "followup", label: "Follow-up" },
];

/** The delivery verdict for a doctor's answer. Undelivered is a real breach -> crit. */
function DeliveryTag({ delivery }: { delivery: AnsweredChat["delivery"] }) {
  if (delivery === "undelivered") {
    return (
      <span
        data-testid="pulse-delivery-undelivered"
        className="shrink-0 whitespace-nowrap text-[11px] font-semibold text-crit-600 dark:text-red-400"
      >
        ⚠ not delivered
      </span>
    );
  }
  if (delivery === "delivered") {
    return (
      <span
        data-testid="pulse-delivery-delivered"
        className="shrink-0 whitespace-nowrap text-[11px] font-medium text-clinic-400 dark:text-gray-500"
      >
        ✓ delivered
      </span>
    );
  }
  return null; // unknown (app push / no outbound WhatsApp record) — claim nothing
}

export default function Pulse({
  counts,
  countsLoading,
  answered,
  answeredLoading,
  answeredError,
}: {
  counts: PendingCounts | null;
  countsLoading: boolean;
  answered: AnsweredChat[];
  answeredLoading: boolean;
  answeredError: boolean;
}) {
  const now = Date.now();

  return (
    <section
      data-testid="worklist-pulse"
      aria-label="Clinic pulse"
      className="mt-6 grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-clinic-200 bg-clinic-100 shadow-sm dark:border-navy-700 dark:bg-navy-700 sm:grid-cols-2"
    >
      {/* ---- Pending now ---- */}
      <div className="bg-white p-5 dark:!bg-navy-800">
        <h2 className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-clinic-500 dark:text-gray-400">
          Pending now
        </h2>
        {countsLoading ? (
          <p className="mt-3 text-sm text-clinic-500 dark:text-gray-400">Loading…</p>
        ) : counts ? (
          <dl className="mt-3 space-y-2.5" data-testid="pulse-pending">
            {PENDING_ROWS.map(({ key, label }) => {
              const b = counts[key];
              return (
                <div
                  key={key}
                  className="flex items-baseline justify-between gap-3"
                  data-testid={`pulse-pending-${key}`}
                >
                  <dt className="text-sm text-clinic-600 dark:text-gray-300">
                    {label}
                  </dt>
                  <dd className="flex items-baseline gap-2 tabular-nums">
                    <span className="text-base font-bold text-clinic-900 dark:text-white">
                      {b.pending}
                    </span>
                    {b.actionable > 0 && (
                      <span className="text-[11px] font-semibold text-crit-600 dark:text-red-400">
                        {b.actionable} past SLA
                      </span>
                    )}
                  </dd>
                </div>
              );
            })}
          </dl>
        ) : (
          <p className="mt-3 text-sm text-clinic-500 dark:text-gray-400">
            Pending counts unavailable.
          </p>
        )}
      </div>

      {/* ---- Answered recently ---- */}
      <div className="bg-white p-5 dark:!bg-navy-800">
        <h2 className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-clinic-500 dark:text-gray-400">
          Answered recently
        </h2>
        {answeredLoading ? (
          <p className="mt-3 text-sm text-clinic-500 dark:text-gray-400">Loading…</p>
        ) : answeredError ? (
          <p className="mt-3 text-sm text-clinic-500 dark:text-gray-400">
            Recent answers unavailable.
          </p>
        ) : answered.length === 0 ? (
          <p className="mt-3 text-sm text-clinic-500 dark:text-gray-400">
            No answers yet in this window.
          </p>
        ) : (
          <ul className="mt-3 space-y-2" data-testid="pulse-answered">
            {answered.slice(0, 6).map((a) => {
              const mins = minutesSince(a.answeredAt, now);
              return (
                <li
                  key={a.id}
                  className="flex items-baseline justify-between gap-3"
                  data-testid="pulse-answered-row"
                >
                  <span className="min-w-0 truncate text-sm text-clinic-700 dark:text-gray-200">
                    <span className="font-medium text-clinic-900 dark:text-white">
                      {a.name}
                    </span>
                    <span className="text-clinic-500 dark:text-gray-400">
                      {" · "}
                      {typeLabel(a.type)}
                      {a.doctorName ? ` · ${a.doctorName}` : ""}
                    </span>
                  </span>
                  <span className="flex shrink-0 items-baseline gap-2">
                    <DeliveryTag delivery={a.delivery} />
                    <span className="whitespace-nowrap text-xs tabular-nums text-clinic-400 dark:text-gray-500">
                      {mins >= 0 ? `${formatWaiting(mins)} ago` : "—"}
                    </span>
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
